import { authenticatedFetch } from "./authenticatedFetch";
import { parseApiResponseBody, isHtmlApiResponse } from "../utils/parseApiResponseBody";
import { formatApiErrorMessage } from "../utils/apiErrorMessage";
import {
  resolveApiErrorMessage,
  fetchDocumentGenerationErrorFallback,
} from "../utils/apiErrorMessages";
import { fetchAllEvaluatedKeyPages } from "../utils/paginatedFetch";
import {
  DOCUMENT_SEARCH_MAX_RESULTS,
  DOCUMENT_SEARCH_PAGE_SIZE,
  buildAdvancedSearchQueryParams,
} from "../utils/documentAdvancedSearch";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

class ContractService {
  /* ==========================
     DOCUMENT LISTING
  ========================== */

  async listDocuments({ lastEvaluatedKey, pageSize = 20 } = {}) {
    try {
      const params = new URLSearchParams();
      params.append('page_size', String(pageSize));

      if (lastEvaluatedKey) {
        params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey));
      }

      const response = await authenticatedFetch(
        `${API_BASE_URL}/get-documents?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        }
      );

      const data = await parseApiResponseBody(response);

      if (!response.ok || isHtmlApiResponse(response, data)) {
        return {
          success: false,
          error: formatApiErrorMessage(data, 'Erro ao buscar documentos'),
        };
      }

      return {
        success: true,
        data: {
          items: data?.items || [],
          lastEvaluatedKey: data?.last_evaluated_key || null,
          count: data?.count || 0,
        },
      };
    } catch (error) {
      console.error('listDocuments error:', error);
      return {
        success: false,
        error: formatApiErrorMessage(error, 'Erro ao buscar documentos'),
      };
    }
  }

  /** Fetches every page of GET /get-documents (for screens that need the full list client-side). */
  async listAllDocuments() {
    return fetchAllEvaluatedKeyPages((lastEvaluatedKey) =>
      this.listDocuments({ lastEvaluatedKey })
    );
  }

  async searchDocumentsPage(criteria, { lastEvaluatedKey, pageSize = DOCUMENT_SEARCH_PAGE_SIZE } = {}) {
    try {
      const params = new URLSearchParams();
      const query = buildAdvancedSearchQueryParams(criteria || {});
      Object.entries(query).forEach(([key, value]) => {
        if (value != null && String(value).trim() !== '') {
          params.append(key, String(value));
        }
      });
      params.append('page_size', String(pageSize));
      if (lastEvaluatedKey) {
        params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey));
      }

      const response = await authenticatedFetch(
        `${API_BASE_URL}/search-documents?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        }
      );

      if (!response.ok) {
        let errorBody = null;
        try {
          errorBody = await response.json();
        } catch { }
        const message =
          errorBody?.message ||
          errorBody?.error ||
          'Erro na busca avançada de documentos';
        return { success: false, error: message };
      }

      const data = await response.json();
      return {
        success: true,
        data: {
          items: data.items || [],
          lastEvaluatedKey: data.last_evaluated_key || null,
          count: data.count || 0,
          searchMeta: data.search_meta || null,
        },
      };
    } catch (error) {
      console.error('searchDocumentsPage error:', error);
      return {
        success: false,
        error: 'Erro inesperado na busca avançada',
      };
    }
  }

  /**
   * Loads every page of a search query into memory (cap DOCUMENT_SEARCH_MAX_RESULTS).
   */
  async searchAllDocuments(criteria, { maxResults = DOCUMENT_SEARCH_MAX_RESULTS } = {}) {
    const items = [];
    let lastEvaluatedKey = null;
    let truncated = false;
    let searchMeta = null;

    while (items.length < maxResults) {
      const page = await this.searchDocumentsPage(criteria, { lastEvaluatedKey });
      if (!page.success) {
        return page;
      }
      searchMeta = page.data.searchMeta ?? searchMeta;
      const pageItems = page.data.items || [];
      items.push(...pageItems);
      lastEvaluatedKey = page.data.lastEvaluatedKey;
      if (!lastEvaluatedKey) {
        break;
      }
      if (items.length >= maxResults) {
        truncated = true;
        break;
      }
    }

    return {
      success: true,
      data: {
        items: items.slice(0, maxResults),
        count: Math.min(items.length, maxResults),
        truncated,
        searchMeta,
      },
    };
  }

  async listDocumentsByTemplatePage(template, { lastEvaluatedKey } = {}) {
    try {
      const params = new URLSearchParams();
      params.append('template', template);
      if (lastEvaluatedKey) {
        params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey));
      }

      const response = await authenticatedFetch(
        `${API_BASE_URL}/get-documents-by-template?${params.toString()}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        let errorBody = null;
        try {
          errorBody = await response.json();
        } catch { }
        return {
          success: false,
          error: errorBody || 'Erro ao buscar documentos do modelo',
        };
      }

      const data = await response.json();
      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];

      return {
        success: true,
        data: {
          items,
          lastEvaluatedKey: data.last_evaluated_key || null,
          count: data.count ?? items.length,
        },
      };
    } catch (error) {
      console.error('listDocumentsByTemplatePage error:', error);
      return {
        success: false,
        error: 'Erro inesperado ao buscar documentos do modelo',
      };
    }
  }

  /**
   * List saved documents for the current user and template (all pages).
   * GET /get-documents-by-template?template=...
   */
  async getDocumentsByTemplate(template) {
    return fetchAllEvaluatedKeyPages((lastEvaluatedKey) =>
      this.listDocumentsByTemplatePage(template, { lastEvaluatedKey })
    );
  }

  /* ==========================
     DOWNLOAD DOCUMENT
  ========================== */

  async downloadDocument({ s3Key, name }) {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/download-document`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ s3_key: s3Key }),
        }
      );

      if (!response.ok) {
        let errorBody = null;
        try {
          errorBody = await response.json();
        } catch { }
        return { success: false, error: errorBody };
      }

      const blob = await response.blob();

      return {
        success: true,
        data: {
          blob,
          fileName: `${name}.pdf`,
        },
      };
    } catch (error) {
      console.error('downloadDocument error:', error);
      return { success: false, error: 'Erro inesperado ao baixar documento' };
    }
  }

  /* ==========================
     DELETE DOCUMENT
  ========================== */

  async deleteDocument(s3Key) {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/delete-document`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ s3_key: s3Key }),
        }
      );

      if (!response.ok) {
        let errorBody = null;
        try {
          errorBody = await response.json();
        } catch { }
        return { success: false, error: errorBody };
      }

      return { success: true };
    } catch (error) {
      console.error('deleteDocument error:', error);
      return { success: false, error: 'Erro inesperado ao excluir documento' };
    }
  }

  /* ==========================
     GET DOCUMENT BY JOB ID
  ========================== */

  async getDocumentByJobId(jobId) {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/documents/${encodeURIComponent(jobId)}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        let errorBody = null;
        try {
          errorBody = await response.json();
        } catch { }
        return {
          success: false,
          error: errorBody || 'Erro ao buscar documento',
        };
      }

      const data = await response.json();

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error('getDocumentByJobId error:', error);
      return {
        success: false,
        error: 'Erro inesperado ao buscar documento',
      };
    }
  }


  async generateContract(payload) {
    console.log('generateContract payload:', payload);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/fill-document`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await parseApiResponseBody(response);

      if (isHtmlApiResponse(response, data)) {
        const fallbackMessage = await fetchDocumentGenerationErrorFallback();
        return {
          success: false,
          error:
            fallbackMessage ||
            "Não foi possível gerar o documento. Verifique o limite da sua licença ou tente novamente.",
        };
      }

      if (!response.ok) {
        return {
          success: false,
          error: resolveApiErrorMessage(data),
        };
      }

      const jobId = data?.jobId ?? data?.job_id;
      if (!jobId) {
        return {
          success: false,
          error: resolveApiErrorMessage(data),
        };
      }

      return {
        success: true,
        data: { ...data, jobId },
      };
    } catch (error) {
      console.error("generateContract error:", error);

      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao gerar contrato"),
      };
    }
  }

  async generateDraft(payload) {
    console.log('generateDraft payload:', payload);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/documents/save-draft`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorBody = null;

        try {
          errorBody = await response.json();
        } catch { }

        return {
          success: false,
          error: errorBody?.message || "Erro ao salvar rascunho",
        };
      }

      const data = await response.json();

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error("generateDraft error:", error);

      return {
        success: false,
        error: "Erro inesperado ao salvar rascunho",
      };
    }
  }
}

export default new ContractService();
